import { BaseTable } from "@/components/BaseTable";
import CustomButton from "@/components/common/Button";
import { LetterHeadService } from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import navigationRoutes from "@/utils/navigationRoutes";
import { LetterHead } from "@/utils/types/latterHead";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ActiveToggleButton from "../../components/ActiveToggleButton";
import { tableName } from "../../utils/constant/tableName";

const LetterHeadListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["letterHeads", page, pageSize],
    queryFn: () => LetterHeadService.getAllLetterHeads({ page, pageSize }),
  });

  const handleDelete = async (id: string) => {
    try {
      await LetterHeadService.deleteLetterHead(id);
      queryClient.invalidateQueries({ queryKey: ["letterHeads"] });
    } catch (error: any) {
      console.error("Failed to delete letter head:", error);
    }
  };

  const gridData = data?.results ?? [];
  const total = data?.count ?? 0;

  const columns: ProColumns<LetterHead>[] = [
    {
      title: "Letter Head Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name!.localeCompare(b.name!),
    },
    {
      title: "Is Active",
      width: 120,
      render: (record: any) => {
        return (
          <ActiveToggleButton
            table={tableName.letter_head}
            pk={record.id}
            is_active={record.is_active}
          />
        );
      },
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => navigate(navigationRoutes.LETTER_HEAD_EDIT(record.id))}
          type="text"
          title="Edit Letter Head"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this letter head?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete Letter Head"
          />
        </Popconfirm>,
      ],
    },
  ];

  return (
    <BaseTable<LetterHead>
      loading={isLoading}
      columns={columns}
      dataSource={gridData}
      page={page}
      pageSize={pageSize}
      total={total}
      rowKey="id"
      toolBarRender={() => [
        <CustomButton
          key="add"
          type="primary"
          icon={<Plus size={16} />}
          onClick={() => navigate(navigationRoutes.LETTER_HEAD_ADD)}
          title="Add New Letter Head"
        >
          Add Letter Head
        </CustomButton>,
      ]}
      headerTitle="Letter Head List"
      onPageChange={(page, size) => {
        setPage(page);
        setPageSize(size ?? 10);
        searchParams.set("page", page.toString());
        searchParams.set("pageSize", size.toString());
        navigate(`${navigationRoutes.LETTER_HEAD}?${searchParams.toString()}`);
      }}
      request={() => refetch()}
      tableName={gridName.letterHeadTable}
    />
  );
};

export default LetterHeadListPage;
